import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { PublicReadUnavailable, fetchPublicRead } from "@/lib/public-read";
import type { Json } from "@/integrations/supabase/types";
import { fetchUserDataBatch } from "@/lib/user-data-helpers";
import { apiGet, apiPost } from "@/lib/api-client";

export type DailyTopic = {
  id: string;
  user_id: string;
  created_at: string;
  topic: string;
  slug: string;
  questions: Json;
  vocabulary: Json;
  category_id: string | null;
  category_name: string | null;
  creator_email: string;
  creator_avatar_url: string | null;
  creator_display_name: string;
  view_count: number;
  like_count: number;
  comment_count: number;
  banner_url: string | null;
  score: number;
};

export const useDailyTopic = () => {
  return useQuery({
    queryKey: queryKeys.dailyTopic.all(),
    queryFn: async (): Promise<DailyTopic | null> => {
      let rows: Array<Record<string, unknown>> | null = null;

      try {
        const cached = await fetchPublicRead<{ data: Array<Record<string, unknown>> }>("topic-of-day");
        rows = cached.data ?? [];
      } catch (error) {
        if (!(error instanceof PublicReadUnavailable)) throw error;
        console.warn("[topic-of-day] cached read unavailable, using direct query:", error.message);

        // Call the get_topic_of_day RPC function
        // The typed endpoint wraps the same SQL function.
        const topic = await apiGet<Record<string, unknown> | null>("/v1/topics/today").catch((error: unknown) => {
          console.error("Error fetching daily topic:", error);
          return null;
        });

        if (!topic) return null;
        rows = [topic];
      }

      // If no data or empty array, return null
      if (!rows || rows.length === 0) {
        return null;
      }

      // The function returns an array, get the first (and only) result
      const topicData = rows[0] as any;

      // Fetch user data to get creator display name
      const userDataMap = await fetchUserDataBatch([topicData.user_id]);
      const userData = userDataMap.get(topicData.user_id);

      return {
        id: topicData.id,
        user_id: topicData.user_id,
        created_at: topicData.created_at,
        topic: topicData.topic,
        slug: topicData.slug,
        // RPC now returns counts; expand to length-only placeholders so the
        // card's `.length`/getContentCounts keeps working without fetching content.
        questions: Array.from({ length: topicData.question_count ?? 0 }),
        vocabulary: Array.from({ length: topicData.vocabulary_count ?? 0 }),
        category_id: topicData.category_id,
        category_name: topicData.category_name,
        creator_email: userData?.email || topicData.creator_email || "Unknown",
        creator_avatar_url: userData?.avatar_url || topicData.creator_avatar_url || null,
        creator_display_name: userData?.display_name || topicData.creator_email?.split('@')[0] || "Anonymous",
        view_count: Number(topicData.view_count) || 0,
        like_count: Number(topicData.like_count) || 0,
        comment_count: Number(topicData.comment_count) || 0,
        banner_url: topicData.banner_url || null,
        score: 0,
      };
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes (topic changes daily)
    refetchOnWindowFocus: false,
    retry: false, // Don't retry on error, just return null
  });
};

